/**
 * M07 批量回归触发器 · S2 Day 8b
 *
 * 触发 POST /admin/v1/eval/regress · 三档 mode 切换
 * 埋点：regress_run（T11 §12.3 关键操作）
 */
import { Play } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import type { RegressionMode, RegressionResponse } from "@/lib/api/eval";
import { useRegress } from "@/lib/api/eval";

interface Props {
	onResult: (resp: RegressionResponse) => void;
}

const MODE_LABEL: Record<RegressionMode, string> = {
	intent: "仅意图",
	playbook: "仅方案",
	both: "意图 + 方案",
};

// TODO(S2 Day 9+)：admin_eval_regress_run / admin_snapshot_save / admin_snapshot_restore
// 三个事件需要先在 packages/analytics/src/events.ts 注册到 AdminEvent 联合类型 + events.test.ts 补例
// 然后在此处 + snapshot-panel.tsx 接 trackEvent。Day 8b 范围内仅完成 UI + API 链路。

export function RegressTrigger({ onResult }: Props) {
	const [mode, setMode] = useState<RegressionMode>("both");
	const regress = useRegress();

	const handleRun = () => {
		regress.mutate(
			{ mode },
			{
				onSuccess: (data) => onResult(data),
			},
		);
	};

	return (
		<div
			className="flex flex-wrap items-center gap-3 rounded-md border border-surface-border bg-surface-card p-4"
			data-testid="m07-regress-trigger"
		>
			<div className="text-sm text-text-secondary">
				模式
				<span className="ml-2 inline-flex overflow-hidden rounded-md border border-surface-border">
					{(Object.keys(MODE_LABEL) as RegressionMode[]).map((m) => (
						<button
							key={m}
							type="button"
							data-testid={`m07-mode-${m}`}
							onClick={() => setMode(m)}
							className={`px-3 py-1 text-xs transition-colors ${
								mode === m
									? "bg-brand-accent text-text-inverse"
									: "bg-surface-background text-text-secondary hover:bg-surface-subtle"
							}`}
						>
							{MODE_LABEL[m]}
						</button>
					))}
				</span>
			</div>

			<Button
				type="button"
				variant="accent"
				size="sm"
				onClick={handleRun}
				disabled={regress.isPending}
				data-testid="m07-run-regress"
			>
				<Play className="mr-1.5 h-3.5 w-3.5" />
				{regress.isPending ? "回归中…" : "跑批量回归"}
			</Button>

			{regress.isError ? (
				<span className="text-sm text-brand-danger" data-testid="m07-regress-error">
					回归失败：{regress.error instanceof Error ? regress.error.message : String(regress.error)}
				</span>
			) : null}
		</div>
	);
}
